// Copyright (c) 2025 Audere, All Rights Reserved

import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { generatePath, useLocation, useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";

import ProjectStepper from "../components/CreateProjectStepper";
import AIModelSetup from "../components/AIModelSetup";
import ProjectNameForm from "../components/ProjectNameForm";
import {
  ModelType,
  PROJECT_FLOW_QUERY_KEYS,
  PROJECT_FLOW_STEP_VALUES,
  stepToQueryValue,
  type ListProjectsData,
  type ProjectBasic,
  type SelectedTestIds,
  type StepType,
  type TestItem,
} from "../types/projects";
import {
  useCreateProjectMutation,
  useLazyGetProjectQuery,
  useListProjectsQuery,
  useUpdateProjectMutation,
} from "../features/data/projectApiSlice";
import { selectCurrentOrganizationId } from "../features/auth/authSlice";

import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import AddNewTest from "../components/AddNewTest";
import { clearProject, selectProjectName } from "../features/data/projectSlice";
import { useAppDispatch } from "../features/store";
import {
  CREATE_PROJECT_WITHOUT_TEST_PATH,
  DASHBOARD_PATH,
  UPDATE_PROJECT_WITHOUT_TEST_PATH,
} from "../utils/constants";

const steps = ["Project name", "A.I model setup"];

const CreatePage = () => {
  const [createProject, createProjectResult] = useCreateProjectMutation();
  const {
    data: createProjectData,
    error: createError,
    isSuccess: isCreateSuccess,
  } = createProjectResult;
  const location = useLocation();
  const navigate = useNavigate();
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);
  const params = useParams();
  const dispatch = useAppDispatch();
  const projectName = useSelector(selectProjectName);

  const [updateProject, updateProjectResult] = useUpdateProjectMutation();
  const {
    data: updatedProjectData,
    error: updateError,
    isSuccess: isUpdateSuccess,
  } = updateProjectResult;
  const [getProject, { data: getProjectData }] = useLazyGetProjectQuery();
  const { data: listProjectsData } = useListProjectsQuery();

  const [currentStep, setCurrentStep] = useState(0);
  const [isAddNewTest, setIsAddNewTest] = useState(false);
  const [projectData, setProjectData] = useState<ProjectBasic>({ projectName: "", uid: "" });
  const [newTests, setNewTests] = useState<TestItem[]>([]);

  const updateQueryParam = useCallback(
    (key: string, value: string) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(key, value);
      const { orgUid = "", projectUid = "" } = params;
      const newPath = generatePath(
        projectUid ? UPDATE_PROJECT_WITHOUT_TEST_PATH : CREATE_PROJECT_WITHOUT_TEST_PATH,
        { orgUid, projectUid }
      );
      const newFullPath = `${newPath}?${searchParams.toString()}`;
      const currentFullPath = `${location.pathname}${location.search}`;
      if (currentFullPath !== newFullPath) {
        navigate(newFullPath);
      }
    },
    [location.pathname, location.search, navigate, params]
  );
  const handleStep = useCallback(
    (step: number, data?: ProjectBasic) => {
      updateQueryParam(PROJECT_FLOW_QUERY_KEYS.STEP, stepToQueryValue[step as StepType]);
      setCurrentStep(step);
      if (data) {
        setProjectData(data);
      }
    },
    [setCurrentStep, setProjectData, updateQueryParam]
  );
  const handleEditProject = useCallback(
    (step: number) => {
      setCurrentStep(step);
    },
    [setCurrentStep]
  );
  const handleCreateProject = useCallback(
    (selectedTests: TestItem[]) => {
      createProject({
        projectName: projectData.projectName,
        rdts: selectedTests.map(test => ({
          organization_uid: test.organization_uid,
          rdt_uid: test.rdt_uid,
        })) as SelectedTestIds[],
        modelType: getModelType(selectedTests),
      });
    },
    [createProject, projectData.projectName]
  );
  const handleAddNewTest = useCallback(() => {
    if (!isAddNewTest) {
      updateQueryParam(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.ADD_NEW_TESTS);
    }
    setIsAddNewTest(prev => !prev);
  }, [isAddNewTest, updateQueryParam]);
  const handleUpdateProject = useCallback(
    (selectedTests: TestItem[]) => {
      updateProject({
        projectUid: params.projectUid as string,
        projectName: projectData.projectName,
        rdts: selectedTests.map(test => ({
          organization_uid: test.organization_uid,
          rdt_uid: test.rdt_uid,
        })) as SelectedTestIds[],
        modelType: getModelType(selectedTests),
      });
    },
    [updateProject, projectData.projectName, params]
  );
  const triggerGetProject = useCallback(
    (projectUid: string, isNavigateNeeded?: boolean) => {
      getProject({ projectUid })
        .unwrap()
        .then(response => {
          const test = response?.project?.rdts?.[0];
          setProjectData(prev => ({ ...prev, projectName: response?.project?.name }));
          if (!test || !isNavigateNeeded) return;
          navigate(
            generatePath(DASHBOARD_PATH, {
              orgUid: currentOrganizationId,
              projectUid: projectUid,
              testUid: test.rdt_uid,
            })
          );
        });
    },
    [currentOrganizationId, navigate, getProject]
  );
  const handleCancel = useCallback(() => {
    const { projectUid } = params;
    triggerGetProject((projectUid || listProjectsData?.projects[0].project_uid) as string, true);
  }, [listProjectsData?.projects, params, triggerGetProject]);
  const handleStepRender = useCallback(() => {
    switch (currentStep) {
      case 0:
        return (
          <ProjectNameForm
            projectData={projectData}
            handleStep={handleStep}
            onCancel={handleCancel}
            listProjectsData={listProjectsData as ListProjectsData}
          />
        );
      case 1:
        return (
          <AIModelSetup
            handleStep={handleStep}
            projectDetails={projectData}
            handleEditProject={handleEditProject}
            onContinue={params.projectUid ? handleUpdateProject : handleCreateProject}
            error={createError || updateError}
            onAddNewTest={handleAddNewTest}
            setNewTests={setNewTests}
            newTests={newTests}
            currentProjectData={getProjectData}
            onCancel={handleCancel}
          />
        );
      default:
        return null;
    }
  }, [
    currentStep,
    handleStep,
    handleAddNewTest,
    projectData,
    handleEditProject,
    handleCreateProject,
    createError,
    updateError,
    params,
    handleUpdateProject,
    newTests,
    getProjectData,
    handleCancel,
    listProjectsData,
  ]);
  const getModelType = (testItems: TestItem[]) => {
    const hasFoundation = testItems.some(({ condition }) =>
      condition.toLowerCase().includes("foundation")
    );
    const hasOther = testItems.some(
      ({ condition }) => !condition.toLowerCase().includes("foundation")
    );
    if (hasFoundation && hasOther) return ModelType.COMBINED;
    return hasFoundation ? ModelType.RDT_GENERIC : ModelType.RDT_SPECIFIC;
  };

  useEffect(() => {
    if (isCreateSuccess && createProjectData) {
      dispatch(clearProject());
      triggerGetProject(createProjectData.project_uid, true);
    }
  }, [createProjectData, isCreateSuccess, dispatch, triggerGetProject]);
  useEffect(() => {
    if (isUpdateSuccess && updatedProjectData) {
      dispatch(clearProject());
      triggerGetProject(updatedProjectData.project_uid, true);
    }
  }, [updatedProjectData, isUpdateSuccess, dispatch, triggerGetProject]);
  useEffect(() => {
    const projectUid = params.projectUid;
    if (projectUid) {
      triggerGetProject(projectUid);
    }
  }, [params.projectUid, triggerGetProject]);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const step = searchParams.get(PROJECT_FLOW_QUERY_KEYS.STEP);
    if (
      step === PROJECT_FLOW_STEP_VALUES.PROJECT_NAME ||
      step === PROJECT_FLOW_STEP_VALUES.AI_MODEL_SETUP
    ) {
      handleStep(step === PROJECT_FLOW_STEP_VALUES.PROJECT_NAME ? 0 : 1);
      setIsAddNewTest(false);
    } else if (
      step === PROJECT_FLOW_STEP_VALUES.ADD_NEW_TESTS ||
      step === PROJECT_FLOW_STEP_VALUES.IMAGE_UPLOAD
    ) {
      setIsAddNewTest(true);
    }
  }, [location.search, handleStep]);
  useEffect(() => {
    if (projectName) {
      setProjectData(prev => ({ ...prev, projectName }));
    }
  }, [projectName]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.outerContainer}>
        {!isAddNewTest ? (
          <>
            <Box sx={styles.innerContainer}>
              <ProjectStepper steps={steps} currentStep={currentStep} />
            </Box>
            <Box sx={{ mt: 4 }}>{handleStepRender()}</Box>
          </>
        ) : (
          <AddNewTest />
        )}
      </Box>
    </Box>
  );
};

export default CreatePage;

const styles = createStyleSheet({
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    p: 4,
    background: theme.palette.grey[100],
    overflowY: "auto",
  },
  outerContainer: {
    width: "100%",
    maxWidth: theme.spacing(95),
  },
  innerContainer: {
    width: "100%",
    maxWidth: theme.spacing(50),
  },
});
